/* eslint-disable no-param-reassign */
/* eslint-disable import/no-cycle */
import { i18n } from '@/boot/i18n';
import { setSentryUser } from '@/boot/sentry';
import OrganizationService from '../../../services/organization';
import * as types from './mutationTypes';
import ErrorHandler from '../../../tools/errorHandler';
import { EMAILS, REQUIRED } from '../validator/methods';
import { getIdentificatorCustomDefaultState, getOrganizationCreateDefaultState } from './organizationState';

const identificatorValidator = () => ({
  nameValidator: {
    field: 'identificatorCustom.name',
    validations: [{ method: REQUIRED, message: i18n.t('general.rules.required') }],
    meta: { custom: true },
  },
  typeValidator: {
    field: 'identificatorCustom.type',
    validations: [{ method: REQUIRED, message: i18n.t('general.rules.required') }],
    meta: { custom: true },
  },
});

const validators = () => ({
  nameValidator: {
    field: 'name',
    validations: [{ method: REQUIRED, message: i18n.t('general.rules.required') }],
    meta: { custom: false },
  },
  segmentValidator: {
    field: 'segment',
    validations: [{ method: REQUIRED, message: i18n.t('general.rules.required') }],
    meta: { custom: false },
  },
  offsetValidator: {
    field: 'primary_time_offset',
    validations: [{ method: REQUIRED, message: i18n.t('general.rules.required') }],
    meta: { custom: false },
  },
  languageValidator: {
    field: 'primary_language',
    validations: [{ method: REQUIRED, message: i18n.t('general.rules.required') }],
    meta: { custom: false },
  },
  usersAdminValidator: {
    field: 'users_admin_email',
    validations: [{ method: EMAILS, message: i18n.t('general.rules.email') }],
    meta: { custom: false },
  },
});

export default {
  initListState({ commit }) {
    commit(types.SET_ORGANIZATIONS, []);
    commit(types.SET_EXPORT_RANGE, { startDate: null, endDate: null });
  },
  clearListState({ commit }) {
    commit(types.CLEAR_ORGANIZATIONS);
    commit(types.CLEAR_EXPORT_RANGE);
  },
  initNewState({ commit, dispatch }) {
    commit(types.SET_ORGANIZATION, getOrganizationCreateDefaultState());
    commit(types.SET_IDENTIFICATOR_CUSTOM, getIdentificatorCustomDefaultState());
    commit(types.SET_SEGMENTS, []);

    dispatch('user/setTrackUsers', [], { root: true });
    const validator = validators();
    dispatch('validator/addValidator', validator.nameValidator, { root: true });
    dispatch('validator/addValidator', validator.languageValidator, { root: true });
    dispatch('validator/addValidator', validator.offsetValidator, { root: true });
    dispatch('validator/addValidator', validator.segmentValidator, { root: true });
    dispatch('validator/addValidator', validator.usersAdminValidator, { root: true });

    // Identificator customized
    const validatorIdentificator = identificatorValidator();
    dispatch('validator/addValidator', validatorIdentificator.nameValidator, { root: true });
    dispatch('validator/addValidator', validatorIdentificator.typeValidator, { root: true });
  },
  clearNewState({ commit, dispatch }) {
    commit(types.CLEAR_SEGMENTS);
    commit(types.CLEAR_ORGANIZATION);
    commit(types.CLEAR_IDENTIFICATOR_CUSTOM);
    dispatch('user/clearTrackUsers', null, { root: true });
    dispatch('validator/clearValidator', null, { root: true });
  },
  setOrganizationsSuccess({ commit }, data) {
    commit(types.SET_ORGANIZATIONS, data.organizations);
  },
  setOrganizationSuccess({ dispatch }, organization) {
    dispatch('selectOrganization', organization);
  },
  initUpdateState({ commit, dispatch, state }) {
    commit(types.SET_ORGANIZATION_EDIT, { ...state.organization });
    commit(types.SET_SEGMENTS, []);
    const validator = validators();
    dispatch('validator/addValidator', validator.nameValidator, { root: true });
  },
  clearUpdateState({ commit, dispatch }) {
    commit(types.CLEAR_ORGANIZATION_EDIT);
    commit(types.CLEAR_SEGMENTS);
    dispatch('validator/clearValidator', null, { root: true });
  },
  async create({ dispatch, state }) {
    try {
      const { organization } = state;

      await OrganizationService.create(organization);
      dispatch('ui/showToastSuccess', i18n.t('organization.messages.create_success'), { root: true });
      return true;
    } catch (error) {
      dispatch('ui/showToastError', ErrorHandler.getErrorMessage(error), { root: true });
      return false;
    }
  },
  async getOne({ rootState, dispatch }, organization) {
    try {
      const org = organization || rootState.organization.organization;
      const { data: response } = (await OrganizationService.getOne(org.uuid)) || {};
      const { data } = response || {};
      if (data) {
        const { email, name, uuid } = rootState.profile.profile;

        setSentryUser({
          email, name, uuid, organization: data?.name,
        });

        dispatch('setOrganizationSuccess', data);
      }
    } catch (error) {
      dispatch('ui/showToastError', ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  async getAll({ dispatch }) {
    try {
      const { data: response } = (await OrganizationService.getAll()) || {};
      const { data } = response || {};
      if (data.organizations) {
        dispatch('setOrganizationsSuccess', data);
      }
    } catch (error) {
      dispatch('ui/showToastError', ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  async getSegments({ dispatch, commit }) {
    try {
      const response = await OrganizationService.getSegments();
      if (response.data.data) commit(types.SET_SEGMENTS, response.data.data);
    } catch (error) {
      dispatch('ui/showToastError', ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  async update({ state, dispatch }) {
    const isValid = await dispatch('validateUpdate');
    if (!isValid) {
      return false;
    }
    const { organizationEdit } = state;
    try {
      const response = await OrganizationService.update(organizationEdit);
      if (response.data.data) {
        const updatedOrganization = response.data.data;
        dispatch('updateOrganization', updatedOrganization);
      }
      dispatch('ui/showToastSuccess', i18n.t('organization.messages.update_success'), { root: true });
      return true;
    } catch (error) {
      dispatch('ui/showToastError', ErrorHandler.getErrorMessage(error), { root: true });
      return false;
    }
  },
  setOrganization({ commit }, organization) {
    commit(types.SET_ORGANIZATION, organization);
  },
  selectOrganization({ commit }, organization) {
    commit(types.SET_ORGANIZATION, organization);
    commit(types.SET_ORGANIZATION_STORAGE);
  },
  updateOrganization({ commit, state }, updatedOrganization) {
    const { organization } = state;

    updatedOrganization.permissions = organization.permissions;
    updatedOrganization.user_dashboard = organization.user_dashboard;

    commit(types.SET_ORGANIZATION, updatedOrganization);
    commit(types.SET_ORGANIZATION_STORAGE);
  },
  setExportRange({ commit }, { startDate, endDate }) {
    commit(types.SET_EXPORT_RANGE, { startDate, endDate });
  },
  clearExportRange({ commit }) {
    commit(types.CLEAR_EXPORT_RANGE);
  },
  async setIdentificator({ commit, state, dispatch }) {
    const hasError = await dispatch('validateIdentificatorCustom');
    if (hasError) {
      return false;
    }
    commit(types.SET_FIELD, {
      field: 'identificator',
      value: {
        name: state.identificatorCustom.name,
        type: state.identificatorCustom.type,
      },
    });
    commit(types.SET_IDENTIFICATOR_CUSTOM, getIdentificatorCustomDefaultState());
    return true;
  },
  setField({ commit, dispatch }, { field, value }) {
    commit(types.SET_FIELD, { field, value });
    dispatch('validator/checkValidation', { field, value }, { root: true });
  },
  setEditField({ commit, dispatch }, { field, value }) {
    commit(types.SET_EDIT_FIELD, { field, value });
    dispatch('validator/checkValidation', { field, value }, { root: true });
  },
  setIdentificatorCustomField({ commit }, { field, value }) {
    commit(types.SET_IDENTIFICATOR_CUSTOM_FIELD, { field, value });
  },
  async addUserAdminEmail({ commit, dispatch }, emails) {
    const isValid = await dispatch('validator/validate', { field: 'users_admin_email', value: emails }, { root: true });
    if (isValid) {
      commit(types.SET_USER_ADMIN_EMAIL, emails);
    }
  },
  setUserDashboard({ commit }, dashboardUUID) {
    commit(types.SET_USER_DASHBOARD, dashboardUUID);
  },
  clearSegments({ commit }) {
    commit(types.CLEAR_SEGMENTS);
  },
  validateDataStep({ state, dispatch, rootState }) {
    dispatch('validator/validate', { field: 'name', value: state.organization.name }, { root: true });
    dispatch('validator/validate', { field: 'segment', value: state.organization.segment }, { root: true });
    dispatch(
      'validator/validate',
      { field: 'primary_time_offset', value: state.organization.primary_time_offset },
      { root: true },
    );
    dispatch(
      'validator/validate',
      { field: 'primary_language', value: state.organization.primary_language },
      { root: true },
    );
    const findErrors = !!rootState.validator.errors?.find((error) => !error.meta.custom);
    return !findErrors;
  },
  validateIdentificatorCustom({ dispatch, state, rootState }) {
    dispatch(
      'validator/validate',
      { field: 'identificatorCustom.name', value: state.identificatorCustom.name },
      { root: true },
    );
    dispatch(
      'validator/validate',
      { field: 'identificatorCustom.type', value: state.identificatorCustom.type },
      { root: true },
    );
    return !!rootState.validator.errors?.find((error) => error.meta.custom);
  },
  validateUpdate({ dispatch, state, rootState }) {
    dispatch('validator/validate', { field: 'name', value: state.organizationEdit.name }, { root: true });
    return rootState.validator.errors?.length === 0;
  },
};
